"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import type { ComponentProps } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";

import {
  CurrencyInput,
  HeadingLabelCell,
  LabelCell,
  NumberDisplayCell,
  Table,
  TableHeader,
  Tr,
} from "./shared";

const formSchema = z.object({
  daysOfInventoryOutside: z.number().positive(),
  daysOfSalesOutstanding: z.number().positive(),
  daysPayableOutstanding: z.number().positive(),
});

const defaultValues = {
  daysOfInventoryOutside: 70,
  daysOfSalesOutstanding: 14,
  daysPayableOutstanding: 0,
} as const;

export function CashConversionCycleCalculator() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TableHeader>
          <p className="text-body-md text-text-on-dark sticky top-0 font-semibold">
            {t("cashConversionCycle")}
          </p>
        </TableHeader>
        <Table>
          <colgroup>
            <col className="w-4/6 md:w-3/4" />
            <col className="w-2/6 md:w-1/4" />
          </colgroup>
          <tbody className="bg-surface">
            <Tr>
              <NumberDisplayCell />
              <HeadingLabelCell className="text-end">
                {t("days")}
              </HeadingLabelCell>
            </Tr>
            <FormField
              control={form.control}
              name="daysOfInventoryOutside"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <Tr>
                    <CalculatorLabelCell>
                      <UnstyledFormLabel>{t("dio")}</UnstyledFormLabel>
                    </CalculatorLabelCell>
                    <NumberDisplayCell>
                      <FormControl>
                        <CurrencyInput {...field} />
                      </FormControl>
                    </NumberDisplayCell>
                  </Tr>
                </FormItemWithoutDiv>
              )}
            />
            <FormField
              control={form.control}
              name="daysOfSalesOutstanding"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <Tr>
                    <CalculatorLabelCell>
                      <UnstyledFormLabel>
                        <em>{tCommon("plus")}</em> {t("dso")}
                      </UnstyledFormLabel>
                    </CalculatorLabelCell>
                    <NumberDisplayCell>
                      <FormControl>
                        <CurrencyInput {...field} />
                      </FormControl>
                    </NumberDisplayCell>
                  </Tr>
                </FormItemWithoutDiv>
              )}
            />

            <FormField
              control={form.control}
              name="daysPayableOutstanding"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <Tr className="border-b">
                    <CalculatorLabelCell>
                      <UnstyledFormLabel>
                        <em>{tCommon("minus")}</em> {t("dpo")}
                      </UnstyledFormLabel>
                    </CalculatorLabelCell>
                    <NumberDisplayCell>
                      <FormControl>
                        <CurrencyInput {...field} />
                      </FormControl>
                    </NumberDisplayCell>
                  </Tr>
                </FormItemWithoutDiv>
              )}
            />

            <Tr className="border-b">
              <LabelCell className="font-bold">{t("ccc")}</LabelCell>
              <NumberDisplayCell className="font-bold">
                {form.watch("daysOfInventoryOutside") +
                  form.watch("daysOfSalesOutstanding") -
                  form.watch("daysPayableOutstanding")}
              </NumberDisplayCell>
            </Tr>
          </tbody>
          <tfoot className="py-4">
            <Tr className="border-x-0 border-b-0 md:border-x-0">
              <td colSpan={3} className="px-5 py-3 text-center">
                <button
                  type="submit"
                  className="hidden"
                  onClick={(e) => e.preventDefault()}
                />
                <Button
                  onClick={() => {
                    Object.entries(defaultValues).forEach(([key, value]) => {
                      form.setValue(key as keyof typeof defaultValues, value);
                    });
                  }}
                  variant="link"
                  size="sm"
                  className="text-body"
                >
                  {tCommon("clearResults")}
                </Button>
              </td>
            </Tr>
          </tfoot>
        </Table>
      </form>
    </Form>
  );
}

function CalculatorLabelCell({
  className,
  ...props
}: ComponentProps<typeof LabelCell>) {
  return <LabelCell {...props} className={cn(className, "w-[300px]")} />;
}
